import { c as _c } from "react/compiler-runtime";
import { useEagGridStore } from '../store/useEagGridStore';
import type { ActionCallback } from '../types/ActionCallback';
import type { RowData } from '../types/RowData';
interface UseBulkActionExecutionOptions {
  actionCallback: ActionCallback | undefined;
  rows: RowData[];
  selectedRowIds: Set<string>;
  gridCode: string;
  /** Stable refresh callback — reads from refs at call time, does not change identity. */
  refreshData: () => void;
}

/**
 * Returns a callback that executes a bulk action by calling `actionCallback`
 * with an array of selected row IDs and their data.
 *
 * If the callback returns a Promise, the hook sets `isBulkActionLoading` in the
 * store to `true` until the Promise settles. While loading, subsequent calls are
 * silently ignored (double-click protection).
 *
 * No manual useCallback — React Compiler memoizes this hook's return value automatically.
 */
export function useBulkActionExecution(options) {
  const $ = _c(3);
  const store = useEagGridStore();
  let t0;
  if ($[0] !== options || $[1] !== store) {
    t0 = actionKey => {
      if (!options.actionCallback) {
        return;
      }
      if (store.getState().isBulkActionLoading) {
        return;
      }
      const selectedRows = options.rows.filter(row => options.selectedRowIds.has(row.id));
      const selectedIds = selectedRows.map(_temp);
      const result = options.actionCallback({
        actionKey,
        rowId: selectedIds,
        rowData: selectedRows,
        refreshData: options.refreshData,
        refreshCells: _temp2,
        redrawRows: _temp3,
        deselectAll: () => store.deselectAll(),
        getColumnData: _temp4,
        sourceSearchParams: "",
        queryId: store.getState().queryId,
        actionLock: {
          lock: _temp5,
          unlock: _temp6
        }
      });
      if (result !== null && result !== undefined && typeof (result as Promise<unknown>).then === "function") {
        store.setIsBulkActionLoading(true);
        (result as Promise<string | void>).catch(_temp7).finally(() => {
          store.setIsBulkActionLoading(false);
        });
      }
    };
    $[0] = options;
    $[1] = store;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
function _temp7() {}
function _temp6() {}
function _temp5() {}
function _temp4() {
  return null;
}
function _temp3() {}
function _temp2() {}
function _temp(row_0) {
  return row_0.id;
}